// =============================================================================
// SHANIZ GAMING ADMIN - LOGIN PAGE
// =============================================================================

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  Stack,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  SportsEsportsOutlined,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';

// Dim-lit gamer with RGB keyboard — dark, moody, not a product shot.
const HERO_IMAGE =
  '/login-hero.jpg';

const FEATURES = [
  'Matchmaking',
  'Tournaments',
  'KYC',
  'Wallet',
  'Fraud Shield',
  'Referrals',
  'Payouts',
  'Analytics',
  'Support',
];

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate('/');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '14px 16px',
    borderRadius: 12,
    border: '1px solid #1e293b',
    background: '#0f172a',
    color: '#f8fafc',
    fontSize: 14,
    outline: 'none',
    transition: 'border-color 0.15s, box-shadow 0.15s',
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', bgcolor: '#0a0f1a' }}>
      {/* Left — Hero image */}
      <Box
        sx={{
          display: { xs: 'none', lg: 'block' },
          width: '50%',
          position: 'relative',
        }}
      >
        <Box
          component="img"
          src={HERO_IMAGE}
          alt="Gaming App 2.0"
          sx={{ height: '100%', width: '100%', objectFit: 'cover' }}
        />
        <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(10,22,40,0.82)' }} />
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'flex-end',
            p: 8,
          }}
        >
          <Box maxWidth={480}>
            <Typography
              variant="h3"
              fontWeight={900}
              sx={{ color: '#fff', lineHeight: 1.15, fontSize: { lg: '2.25rem', xl: '2.5rem' } }}
            >
              Kenya's skill-gaming platform, managed end-to-end.
            </Typography>
            <Typography sx={{ mt: 2, color: 'rgba(255,255,255,0.6)', lineHeight: 1.7 }}>
              Oversee matches and tournaments, verify KYC, process wallet payouts, investigate
              fraud, run referrals, handle support — all from one dashboard.
            </Typography>
            <Stack direction="row" spacing={1} mt={4} useFlexGap flexWrap="wrap">
              {FEATURES.map((f) => (
                <Box
                  key={f}
                  sx={{
                    px: 1.5,
                    py: 0.75,
                    borderRadius: 999,
                    bgcolor: 'rgba(255,255,255,0.1)',
                    color: 'rgba(255,255,255,0.82)',
                    fontSize: 12,
                    fontWeight: 600,
                    backdropFilter: 'blur(4px)',
                  }}
                >
                  {f}
                </Box>
              ))}
            </Stack>
          </Box>
        </Box>
      </Box>

      {/* Right — Form */}
      <Box
        sx={{
          width: { xs: '100%', lg: '50%' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          px: { xs: 3, md: 6 },
          py: 6,
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 420 }}>
          <Typography variant="h4" fontWeight={900} sx={{ color: '#f8fafc', mb: 1 }}>
            Admin Login
          </Typography>
          <Typography sx={{ color: '#94a3b8', mb: 5 }}>
            Sign in to the Gaming App 2.0 admin dashboard
          </Typography>

          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 3,
                bgcolor: 'rgba(239,68,68,0.08)',
                color: '#fca5a5',
                border: '1px solid rgba(239,68,68,0.25)',
                '& .MuiAlert-icon': { color: '#fca5a5' },
              }}
            >
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Stack spacing={2.5}>
              <Box>
                <Typography
                  component="label"
                  htmlFor="login-email"
                  sx={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#cbd5e1', mb: 1 }}
                >
                  Email
                </Typography>
                <input
                  id="login-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@shaniz.com"
                  required
                  autoFocus
                  style={inputStyle}
                  onFocus={(e) => { e.currentTarget.style.borderColor = '#2563eb'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(37,99,235,0.2)'; }}
                  onBlur={(e) => { e.currentTarget.style.borderColor = '#1e293b'; e.currentTarget.style.boxShadow = 'none'; }}
                />
              </Box>
              <Box>
                <Typography
                  component="label"
                  htmlFor="login-password"
                  sx={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#cbd5e1', mb: 1 }}
                >
                  Password
                </Typography>
                <Box sx={{ position: 'relative' }}>
                  <input
                    id="login-password"
                    type={showPw ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    required
                    style={{ ...inputStyle, paddingRight: 48 }}
                    onFocus={(e) => { e.currentTarget.style.borderColor = '#2563eb'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(37,99,235,0.2)'; }}
                    onBlur={(e) => { e.currentTarget.style.borderColor = '#1e293b'; e.currentTarget.style.boxShadow = 'none'; }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => setShowPw((v) => !v)}
                    sx={{
                      position: 'absolute',
                      right: 6,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: '#64748b',
                      '&:hover': { color: '#cbd5e1' },
                    }}
                    aria-label="toggle password visibility"
                  >
                    {showPw ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </IconButton>
                </Box>
              </Box>
              <Button
                type="submit"
                disabled={loading}
                fullWidth
                sx={{
                  py: 1.75,
                  mt: 1,
                  borderRadius: 3,
                  bgcolor: '#2563eb',
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: 13,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  '&:hover': { bgcolor: '#1d4ed8' },
                  '&.Mui-disabled': { bgcolor: '#334155', color: '#64748b' },
                }}
              >
                {loading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Sign In'}
              </Button>
            </Stack>
          </form>

          {/* Demo credential cards */}
          <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid #1e293b' }}>
            <Typography sx={{ fontSize: 11, color: '#64748b', mb: 1.5, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
              Demo credentials — click to use
            </Typography>
            <Stack spacing={1}>
              <Box
                onClick={() => { setEmail('admin@shaniz.com'); setPassword('Admin@123456'); }}
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: '#0f172a',
                  border: '1px solid #1e293b',
                  cursor: 'pointer',
                  transition: 'border-color 0.15s',
                  '&:hover': { borderColor: '#2563eb' },
                }}
              >
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#60a5fa', letterSpacing: '0.1em' }}>
                  SUPER ADMIN
                </Typography>
                <Typography sx={{ color: '#cbd5e1', fontFamily: 'monospace', fontSize: 12.5, mt: 0.25 }}>
                  admin@shaniz.com · Admin@123456
                </Typography>
              </Box>
              <Box
                onClick={() => { setEmail('moderator@shaniz.com'); setPassword('Mod@123456'); }}
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: '#0f172a',
                  border: '1px solid #1e293b',
                  cursor: 'pointer',
                  transition: 'border-color 0.15s',
                  '&:hover': { borderColor: '#2563eb' },
                }}
              >
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.1em' }}>
                  MODERATOR
                </Typography>
                <Typography sx={{ color: '#cbd5e1', fontFamily: 'monospace', fontSize: 12.5, mt: 0.25 }}>
                  moderator@shaniz.com · Mod@123456
                </Typography>
              </Box>
            </Stack>
          </Box>
        </Box>

        {/* Logo pinned to bottom-right */}
        <Stack
          direction="row"
          alignItems="center"
          spacing={1.25}
          sx={{ position: 'absolute', bottom: 32, right: 32 }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1.5,
              bgcolor: '#2563eb',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <SportsEsportsOutlined sx={{ fontSize: 22, color: '#fff' }} />
          </Box>
          <Typography sx={{ color: '#f8fafc', fontWeight: 700, fontSize: 14 }}>
            Gaming App 2.0
          </Typography>
        </Stack>
      </Box>
    </Box>
  );
};

export default Login;
